// src/components/DailyDoseTracker.js
// Author: Sunil Jain & Gemini
// FINAL: Rebuilt to handle the per-dose data structure from the new API.

import React from 'react';
import { Box, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

/**
 * Renders a single, small icon based on the dose status.
 */
const DoseIcon = ({ status }) => {
    if (status === 'taken') {
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 12 }} />;
    }
    if (status === 'skipped') {
        return <DoDisturbIcon sx={{ color: 'warning.main', fontSize: 12 }} />;
    }
    // 'missed' or any other status
    return <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: 12 }} />;
};

/**
 * (REBUILT) Displays a grid of icons representing individual dose statuses for a given day.
 * Consumes the new `DailyDoseLog` object: { date, doses: [{ status }, ...], ... }
 */
const DailyDoseTracker = ({ day }) => {
    // If there's no day data or no doses were required, render an empty but correctly styled cell.
    if (!day || !day.doses || day.doses.length === 0) {
        return (
            <Stack sx={{ width: '48px', height: '48px', borderLeft: '1px solid', borderColor: 'divider', alignItems: 'center', justifyContent: 'center' }}>
                 <Box sx={{ width: 24, height: 24, bgcolor: 'action.hover', borderRadius: '4px' }} />
            </Stack>
        );
    }

    // Determine grid layout based on number of doses for a compact display
    const doseCount = day.doses.length;
    let gridTemplateColumns = 'repeat(2, 1fr)';
    if (doseCount <= 2) {
        gridTemplateColumns = `repeat(${doseCount}, 1fr)`;
    } else if (doseCount === 3) {
        gridTemplateColumns = 'repeat(3, 1fr)';
    } else if (doseCount > 4) {
        gridTemplateColumns = 'repeat(3, 1fr)'; // Max 3 columns for larger numbers
    }

    return (
        <Stack
            sx={{
                p: 0,
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderLeft: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplateColumns,
                    gap: '2px',
                    width: '36px',
                    height: '36px',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {day.doses.map((dose, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DoseIcon status={dose.status} />
                    </Box>
                ))}
            </Box>
        </Stack>
    );
};

export default DailyDoseTracker;